import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Navbar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import AdminService from "../../../services/admin.service";

const CounsellingUserMapping = () => {
  const [exams, setExams] = useState<any[]>([]);
  const [selectedExam, setSelectedExam] = useState<any>(null);

  // Step 1: Search counselling user
  const [capSearch, setCapSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [counsellingUsers, setCounsellingUsers] = useState<any[]>([]);
  const [searchDone, setSearchDone] = useState(false);

  // Step 2: Selected counselling user → show matched userData
  const [selectedCounsellingUser, setSelectedCounsellingUser] = useState<any>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [matchedUsers, setMatchedUsers] = useState<any[]>([]);

  // Step 3: Confirm map modal
  const [pendingMap, setPendingMap] = useState<any>(null);
  const [mapping, setMapping] = useState(false);

  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      resetAll();
    }
  }, [selectedExam]);

  const fetchExams = async () => {
    try {
      const res = await AdminService.getAllExamName();
      if (res.status === 200) {
        setExams(
          res.data.data.examName.map((d: any) => ({
            value: d._id,
            label: d.name,
          }))
        );
      }
    } catch {
      toast.error("Failed to load exams");
    }
  };

  const resetAll = () => {
    setCapSearch("");
    setCounsellingUsers([]);
    setSearchDone(false);
    setSelectedCounsellingUser(null);
    setMatchedUsers([]);
    setPendingMap(null);
  };

  const handleSearch = async () => {
    if (!selectedExam) {
      toast.error("Please select an exam first");
      return;
    }
    if (!capSearch.trim()) {
      toast.error("Please enter a CAP Application Number");
      return;
    }

    setSearching(true);
    setSearchDone(false);
    setCounsellingUsers([]);
    setSelectedCounsellingUser(null);
    setMatchedUsers([]);

    try {
      const res = await AdminService.searchCounsellingUsers(
        selectedExam.value,
        capSearch.trim()
      );
      if (res.status === 200) {
        setCounsellingUsers(res.data.data.users || []);
        setSearchDone(true);
      }
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleSelectCounsellingUser = async (user: any) => {
    setSelectedCounsellingUser(user);
    setMatchedUsers([]);

    if (!user.rollNo) {
      toast.error("This counselling user has no rollNo to match against");
      return;
    }

    setLoadingUsers(true);
    try {
      const res = await AdminService.getCounsellingUsersWithVerification(
        selectedExam.value,
        user.rollNo
      );
      if (res.status === 200) {
        setMatchedUsers(res.data.data.users || []);
      }
    } catch {
      toast.error("Failed to load matched users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleMapUser = async () => {
    if (!pendingMap) return;
    setMapping(true);
    try {
      const res = await AdminService.mapUserToCounsellingUser(
        selectedCounsellingUser._id,
        pendingMap._id
      );
      if (res.status === 200) {
        toast.success("User mapped successfully");
        // Update the counselling user in list to reflect userId
        setCounsellingUsers((prev) =>
          prev.map((cu) =>
            cu._id === selectedCounsellingUser._id
              ? { ...cu, userId: pendingMap._id }
              : cu
          )
        );
        setSelectedCounsellingUser((prev: any) =>
          prev ? { ...prev, userId: pendingMap._id } : prev
        );
        setPendingMap(null);
      }
    } catch {
      toast.error("Mapping failed");
    } finally {
      setMapping(false);
    }
  };

  const formatDate = (val: any) =>
    val ? new Date(val).toLocaleDateString() : "-";

  return (
    <>
      <Navbar bg="light" expand="lg" className="navbarWrapper">
        <Container fluid>
          <Navbar.Brand>Counselling User Mapping</Navbar.Brand>
          <div className="d-flex gap-3 align-items-center">
            <Select
              options={exams}
              onChange={(opt) => setSelectedExam(opt)}
              value={selectedExam}
              placeholder="Select Exam"
              styles={{ container: (base: any) => ({ ...base, minWidth: 220 }) }}
            />
          </div>
        </Container>
      </Navbar>

      {/* Search Bar */}
      {selectedExam && (
        <Card className="shadow-sm p-3 mt-3 bg-white rounded mx-3">
          <Card.Body>
            <h6 className="text-primary mb-3">Search by CAP Application Number</h6>
            <div className="d-flex gap-3 align-items-end">
              <Form.Group style={{ minWidth: 300 }}>
                <Form.Label className="xcn-input-label text-secondary">
                  CAP Application Number
                </Form.Label>
                <Form.Control
                  value={capSearch}
                  onChange={(e) => setCapSearch(e.target.value)}
                  placeholder="Enter CAP Application No"
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </Form.Group>
              <Button onClick={handleSearch} disabled={searching}>
                {searching ? <Spinner animation="border" size="sm" /> : "Search"}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Counselling Users Results */}
      {searchDone && (
        <Card className="shadow-sm p-3 mt-3 bg-white rounded mx-3">
          <Card.Body>
            <h6 className="text-secondary mb-3">
              Counselling Users{" "}
              <Badge bg="secondary">{counsellingUsers.length}</Badge>
            </h6>

            {counsellingUsers.length === 0 ? (
              <p className="text-muted">No counselling users found.</p>
            ) : (
              <Row>
                {counsellingUsers.map((cu) => (
                  <Col md={4} key={cu._id} className="mb-3">
                    <Card
                      className={`h-100 border ${
                        selectedCounsellingUser?._id === cu._id
                          ? "border-primary"
                          : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSelectCounsellingUser(cu)}
                    >
                      <Card.Body className="py-2 px-3">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <strong className="text-primary">
                            {cu.capApplicationNumber}
                          </strong>
                          {cu.userId ? (
                            <Badge bg="success">Mapped</Badge>
                          ) : (
                            <Badge bg="warning" text="dark">
                              Unmapped
                            </Badge>
                          )}
                        </div>
                        <div className="small text-secondary">
                          <div>
                            <strong>Name:</strong> {cu.name || "-"}
                          </div>
                          <div>
                            <strong>Roll No:</strong> {cu.rollNo || "-"}
                          </div>
                          <div>
                            <strong>App No:</strong>{" "}
                            {cu.applicationNumber || "-"}
                          </div>
                          <div>
                            <strong>Institute:</strong>{" "}
                            {cu.allotedInstituteName || "-"}
                          </div>
                          <div>
                            <strong>Round:</strong> {cu.round || "-"}
                          </div>
                          <div>
                            <strong>Stream:</strong> {cu.stream || "-"}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Matched UserData */}
      {selectedCounsellingUser && (
        <Card className="shadow-sm p-3 mt-3 bg-white rounded mx-3">
          <Card.Body>
            <h6 className="text-primary mb-1">
              Exam Users matching rollNo:{" "}
              <span className="text-dark">{selectedCounsellingUser.rollNo}</span>
            </h6>
            <p className="text-muted small mb-3">
              Counselling user:{" "}
              <strong>{selectedCounsellingUser.name}</strong> &mdash;{" "}
              {selectedCounsellingUser.capApplicationNumber}
              {selectedCounsellingUser.userId && (
                <Badge bg="success" className="ms-2">
                  Already mapped
                </Badge>
              )}
            </p>

            {loadingUsers && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" /> Loading...
              </div>
            )}

            {!loadingUsers && matchedUsers.length === 0 && (
              <p className="text-muted">
                No users found with this rollNo in the selected exam.
              </p>
            )}

            {!loadingUsers && matchedUsers.length > 0 && (
              <Table bordered hover size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Roll No</th>
                    <th>Reg No</th>
                    <th>Center</th>
                    <th>Shift</th>
                    <th>Vendor</th>
                    <th>Verification</th>
                    <th>Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {matchedUsers.map((u) => {
                    const isCurrentlyMapped =
                      selectedCounsellingUser.userId &&
                      String(selectedCounsellingUser.userId) === String(u._id);
                    return (
                      <tr key={u._id}>
                        <td>{u.name || "-"}</td>
                        <td>{u.rollNo || "-"}</td>
                        <td>{u.regNo || "-"}</td>
                        <td>{u.allocatedCenterId || "-"}</td>
                        <td>{u.shiftId || "-"}</td>
                        <td>{u.vendorName || "-"}</td>
                        <td>
                          {u.hasVerification ? (
                            <Badge bg="success">Yes</Badge>
                          ) : (
                            <Badge bg="danger">No</Badge>
                          )}
                        </td>
                        <td>{formatDate(u.createdAt)}</td>
                        <td>
                          {isCurrentlyMapped ? (
                            <Badge bg="success">Mapped</Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => setPendingMap(u)}
                            >
                              Map
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Confirm Map Modal */}
      <Modal show={!!pendingMap} onHide={() => setPendingMap(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Mapping</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Map user <strong>{pendingMap?.name}</strong> (Roll No:{" "}
            <strong>{pendingMap?.rollNo}</strong>) to counselling user{" "}
            <strong>{selectedCounsellingUser?.name}</strong> (CAP:{" "}
            <strong>{selectedCounsellingUser?.capApplicationNumber}</strong>)?
          </p>
          {selectedCounsellingUser?.userId && (
            <p className="text-warning small">
              This counselling user already has a mapped user. This will
              overwrite the existing mapping.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setPendingMap(null)}
            disabled={mapping}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleMapUser}
            disabled={mapping}
          >
            {mapping ? <Spinner animation="border" size="sm" /> : "Confirm Map"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CounsellingUserMapping;
